import { inject, Injectable, OnDestroy } from '@angular/core';
import { type Schema } from '../../../../amplify/data/resource';
import { generateClient } from 'aws-amplify/api';
import { BehaviorSubject, Subscription } from 'rxjs';
import { UserService } from './user.service';

export interface TenantClientLink {
  id: string;
  tenantId: string;
  clientId: string;
  tenant?: { id: string; name: string } | null;
}

/** What kind of counterparty a CRM entry is. Null reads as 'CLIENT'. */
export type ClientType = 'CLIENT' | 'SUPPLIER' | 'ARCHITECT' | 'ENGINEER' | 'OTHER';

export interface Client {
  id: string;
  name: string;
  type?: ClientType | null;
  email?: string | null;
  phone?: string | null;
  address?: any;
  fullAddress?: string | null;
  /** @deprecated Use tenantIds / TenantClient link table instead */
  tenantId?: string | null;
  tenantIds?: string[];
  tenantLinks?: TenantClientLink[];
}

export interface Todo {
  id: string;
  name: string;
  description?: string | null;
  status?: string | null;
  priority?: string | null;
  dueDate?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  // Optional scope links
  tenantId?: string | null;
  tenant?: { id: string; name: string } | null;
  clientId?: string | null;
  client?: { id: string; name: string } | null;
  projectId?: string | null;
  project?: { id: string; projectName: string } | null;
  creatorId?: string | null;
  nameLower?: string | null;
  validatedBy?: string | null;
  validatedAt?: string | null;
  isDeleted?: boolean | null;
  deletedAt?: string | null;
}

export interface TodoUser {
  id: string;
  todoId: string;
  userId: string;
  user?: any;
}

export interface TodoClient {
  id: string;
  todoId: string;
  clientId: string;
  client?: any;
  todo?: any;
}

@Injectable({
  providedIn: 'root',
})
export class ClientService implements OnDestroy {
  private _client?: ReturnType<typeof generateClient<Schema>>;

  private get client() {
    if (!this._client) {
      this._client = generateClient<Schema>();
    }
    return this._client;
  }

  private clientsSubject = new BehaviorSubject<Client[]>([]);
  clients$ = this.clientsSubject.asObservable();

  private todosSubject = new BehaviorSubject<Todo[]>([]);
  todos$ = this.todosSubject.asObservable();
  private userService = inject(UserService);

  private clientsSubscription?: Subscription;
  private todosSubscription?: Subscription;

  constructor() { }

  ngOnDestroy() {
    this.clientsSubscription?.unsubscribe();
    this.todosSubscription?.unsubscribe();
  }

  resetState() {
    this.clientsSubscription?.unsubscribe();
    this.todosSubscription?.unsubscribe();
    this.clientsSubject.next([]);
    this.todosSubject.next([]);
    this._assignedTodosCache = null;
    this._todosByUserCache = null;
    this._initialized = false;
  }

  private _initialized = false;

  async init() {
    if (this._initialized) return;
    this._initialized = true;
    console.log('[ClientService] Initializing with observeQuery for real-time updates');
    this.startRealTimeObservers();
  }

  private startRealTimeObservers() {
    // Real-time client observation — no selectionSet so all fields are fetched
    this.clientsSubscription?.unsubscribe();
    this.clientsSubscription = this.client.models.Client.observeQuery({
      filter: { isDeleted: { ne: true } }
    }).subscribe({
      next: ({ items }: any) => {
        const clients = items.map((c: any) => ({
          id: c.id,
          name: c.name,
          type: c.type ?? null,
          email: c.email,
          phone: c.phone,
          address: c.address ? (typeof c.address === 'string' ? JSON.parse(c.address) : c.address) : null,
          fullAddress: c.fullAddress,
          tenantId: c.tenantId ?? undefined,
        })) as Client[];
        this.clientsSubject.next(clients);
      },
      error: (error: any) => {
        console.error('[ClientService] Error in clients observeQuery:', error);
      }
    });
  }

  /**
   * Filter clients by tenant.
   * Tries the TenantClient link table first (M:N); falls back to legacy Client.tenantId filter.
   * Pass null to revert to the full real-time observeQuery.
   */
  async filterByTenant(tenantId: string | null) {
    this.clientsSubscription?.unsubscribe();
    if (!tenantId) {
      this.startRealTimeObservers();
      return;
    }
    try {
      // Try TenantClient link table (M:N)
      const clients: Client[] = [];
      let nextToken: string | undefined = undefined;
      do {
        const result: any = await this.client.models.TenantClient.listClientsByTenant(
          { tenantId },
          {
            selectionSet: [
              'id', 'clientId', 'tenantId',
              'client.id', 'client.name', 'client.type', 'client.email', 'client.phone',
              'client.address', 'client.fullAddress', 'client.tenantId',
              'client.isDeleted'
            ],
            limit: 200,
            nextToken,
          }
        );
        for (const link of (result.data || [])) {
          const c = link.client;
          if (!c || c.isDeleted) continue;
          if (clients.some(existing => existing.id === c.id)) continue;
          clients.push({
            id: c.id, name: c.name, type: c.type ?? null, email: c.email, phone: c.phone,
            address: c.address ? (typeof c.address === 'string' ? JSON.parse(c.address) : c.address) : null,
            fullAddress: c.fullAddress, tenantId: c.tenantId ?? undefined,
          });
        }
        nextToken = result.nextToken ?? undefined;
      } while (nextToken);

      this.clientsSubject.next(clients);
    } catch (e) {
      // Fallback: filter by legacy Client.tenantId (works before TenantClient table is deployed)
      console.warn('[ClientService] TenantClient query failed, using legacy filter:', e);
      try {
        const result = await (this.client.models.Client.list as any)({
          filter: { tenantId: { eq: tenantId }, isDeleted: { ne: true } },
        });
        const clients = (result.data || []).map((c: any) => ({
          id: c.id, name: c.name, type: c.type ?? null, email: c.email, phone: c.phone,
          address: c.address ? (typeof c.address === 'string' ? JSON.parse(c.address) : c.address) : null,
          fullAddress: c.fullAddress, tenantId: c.tenantId ?? undefined,
        })) as Client[];
        this.clientsSubject.next(clients);
      } catch (fallbackErr) {
        console.error('[ClientService] filterByTenant fallback also failed:', fallbackErr);
      }
    }
  }

  /**
   * Fetch a page of todos with server-side filtering.
   * Uses GSI index queries when scoping by creatorId or clientId,
   * falling back to a full-scan list() only for the assigned-by-IDs case.
   */
  async fetchTodosPage(options: {
    // Tab scoping
    creatorId?: string;
    ids?: string[];
    userId?: string;  // Optimised: single query via join table
    // Search & filters (server-side)
    search?: string;
    status?: string;
    priority?: string;
    tenantId?: string;
    clientId?: string;
    backlog?: boolean;
    // Pagination
    nextToken?: string | null;
    limit?: number;
  } = {}): Promise<{ todos: Todo[]; nextToken: string | null }> {
    const limit = options.limit ?? 100;
    try {
      // Build additional filter conditions (applied as FilterExpression on top of the index)
      // Always exclude soft-deleted todos
      const conditions: any[] = [{ isDeleted: { ne: true } }];

      if (options.search?.trim()) {
        const q = options.search.trim().toLowerCase();
        conditions.push({ nameLower: { contains: q } });
      }
      if (options.status) conditions.push({ status: { eq: options.status } });
      if (options.priority) conditions.push({ priority: { eq: options.priority } });
      if (options.backlog) {
        conditions.push({ tenantId: { attributeExists: false } });
        conditions.push({ clientId: { attributeExists: false } });
        conditions.push({ projectId: { attributeExists: false } });
      }

      const extraFilter = conditions.length === 0
        ? undefined
        : conditions.length === 1
          ? conditions[0]
          : { and: conditions };

      let data: any[];
      let nextToken: any;
      let errors: any;

      // ── Index query: creatorId GSI ──────────────────────────────────────────
      if (options.creatorId) {
        // Also filter by tenantId/clientId if set (FilterExpression on top of GSI)
        const filterConditions = extraFilter ? [extraFilter] : [];
        if (options.tenantId) filterConditions.push({ tenantId: { eq: options.tenantId } });
        if (options.clientId) filterConditions.push({ clientId: { eq: options.clientId } });
        const filter = filterConditions.length === 0 ? undefined
          : filterConditions.length === 1 ? filterConditions[0]
            : { and: filterConditions };

        const result = await (this.client.models.Todo.listTodoByCreatorId as any)({
          creatorId: options.creatorId,
          filter,
          limit,
          nextToken: options.nextToken ?? undefined,
          selectionSet: ['id', 'name', 'nameLower', 'description', 'status', 'priority', 'dueDate',
            'createdAt', 'updatedAt', 'tenantId', 'clientId', 'projectId', 'creatorId', 'isDeleted'],
        });
        data = result.data; nextToken = result.nextToken; errors = result.errors;

        // ── Index query: clientId GSI ───────────────────────────────────────────
      } else if (options.clientId && !options.ids) {
        const result = await (this.client.models.Todo.listTodoByClientId as any)({
          clientId: options.clientId,
          filter: extraFilter,
          limit,
          nextToken: options.nextToken ?? undefined,
          selectionSet: ['id', 'name', 'nameLower', 'description', 'status', 'priority', 'dueDate',
            'createdAt', 'updatedAt', 'tenantId', 'clientId', 'projectId', 'creatorId', 'isDeleted'],
        });
        data = result.data; nextToken = result.nextToken; errors = result.errors;

        // ── Index query: tenantId GSI ───────────────────────────────────────────
      } else if (options.tenantId && !options.ids && !options.creatorId) {
        const result = await (this.client.models.Todo.listTodoByTenantId as any)({
          tenantId: options.tenantId,
          filter: extraFilter,
          limit,
          nextToken: options.nextToken ?? undefined,
          selectionSet: ['id', 'name', 'nameLower', 'description', 'status', 'priority', 'dueDate',
            'createdAt', 'updatedAt', 'tenantId', 'clientId', 'projectId', 'creatorId', 'isDeleted'],
        });
        data = result.data; nextToken = result.nextToken; errors = result.errors;

        // ── Assigned-by-userId: single query via TodoUser join table ──────────
        // Most efficient: 1 paginated GSI query returns todo data through
        // the GraphQL relation, no secondary fetches needed.
      } else if (options.userId) {
        return this._fetchTodosByUserDirect(options.userId, {
          search: options.search,
          status: options.status,
          priority: options.priority,
          tenantId: options.tenantId,
          clientId: options.clientId,
          backlog: options.backlog,
          nextToken: options.nextToken,
          limit,
        });

        // ── Legacy: assigned-by-IDs (kept for backward compat) ────────────────
      } else if (options.ids?.length) {
        return this._fetchAssignedTodosPage(options.ids, {
          search: options.search,
          status: options.status,
          priority: options.priority,
          tenantId: options.tenantId,
          clientId: options.clientId,
          backlog: options.backlog,
          nextToken: options.nextToken,
          limit,
        });

        // ── Fallback: no scoping — list all ───────────────────────────────────
      } else {
        const result = await (this.client.models.Todo.list as any)({
          filter: extraFilter,
          limit,
          nextToken: options.nextToken ?? undefined,
          selectionSet: ['id', 'name', 'nameLower', 'description', 'status', 'priority', 'dueDate',
            'createdAt', 'updatedAt', 'tenantId', 'clientId', 'projectId', 'creatorId', 'validatedBy', 'validatedAt', 'isDeleted'],
        });
        data = result.data; nextToken = result.nextToken; errors = result.errors;
      }

      if (errors?.length) {
        console.error('[ClientService] fetchTodosPage error:', errors);
      }

      const todos = (data || []).map((t: any) => ({
        id: t.id,
        name: t.name,
        description: t.description,
        status: t.status,
        priority: t.priority,
        dueDate: t.dueDate,
        createdAt: t.createdAt,
        updatedAt: t.updatedAt,
        tenantId: t.tenantId ?? undefined,
        clientId: t.clientId ?? undefined,
        projectId: t.projectId ?? undefined,
        creatorId: t.creatorId ?? undefined,
        nameLower: t.nameLower ?? undefined,
        validatedBy: t.validatedBy ?? undefined,
        validatedAt: t.validatedAt ?? undefined,
      })) as Todo[];

      return { todos, nextToken: nextToken ?? null };
    } catch (e) {
      console.error('[ClientService] fetchTodosPage failed:', e);
      return { todos: [], nextToken: null };
    }
  }

  /**
   * ── OPTIMISED: Single-query fetch via TodoUser join table ──────────────────
   * Uses the userId GSI on TodoUser to fetch all assigned todos with their data
   * in 1 paginated GraphQL query (resolves todo fields through the relation).
   * This is O(user_todos) instead of O(table_size) or O(N) individual gets.
   */
  private _todosByUserCache: { userId: string; todos: Todo[]; cacheKey: string } | null = null;

  private async _fetchTodosByUserDirect(
    userId: string,
    opts: {
      search?: string; status?: string; priority?: string;
      tenantId?: string; clientId?: string; backlog?: boolean;
      nextToken?: string | null; limit: number;
    }
  ): Promise<{ todos: Todo[]; nextToken: string | null }> {
    const cacheKey = JSON.stringify({
      userId,
      search: opts.search, status: opts.status, priority: opts.priority,
      tenantId: opts.tenantId, clientId: opts.clientId, backlog: opts.backlog,
    });

    if (!this._todosByUserCache || this._todosByUserCache.cacheKey !== cacheKey) {
      // Single paginated query — fetches todo data through the GraphQL relation
      const allLinks = await this.getTodosByUser(userId);

      // Extract todos from join-table links
      let todos: Todo[] = allLinks
        .filter(link => link.todo && link.todo.isDeleted !== true)
        .map(link => ({
          id: link.todo.id,
          name: link.todo.name,
          description: link.todo.description,
          status: link.todo.status,
          priority: link.todo.priority,
          dueDate: link.todo.dueDate,
          createdAt: link.todo.createdAt,
          updatedAt: link.todo.updatedAt,
          tenantId: link.todo.tenantId ?? undefined,
          clientId: link.todo.clientId ?? undefined,
          projectId: link.todo.projectId ?? undefined,
          creatorId: link.todo.creatorId ?? undefined,
          nameLower: link.todo.nameLower ?? link.todo.name?.toLowerCase() ?? undefined,
          validatedBy: link.todo.validatedBy ?? undefined,
          validatedAt: link.todo.validatedAt ?? undefined,
        } as Todo));

      // Client-side filters
      if (opts.search?.trim()) {
        const q = opts.search.trim().toLowerCase();
        todos = todos.filter(t => t.nameLower?.includes(q) || t.name?.toLowerCase().includes(q));
      }
      if (opts.status) todos = todos.filter(t => t.status === opts.status);
      if (opts.priority) todos = todos.filter(t => t.priority === opts.priority);
      if (opts.tenantId) todos = todos.filter(t => t.tenantId === opts.tenantId);
      if (opts.clientId) todos = todos.filter(t => t.clientId === opts.clientId);
      if (opts.backlog) todos = todos.filter(t => !t.tenantId && !t.clientId && !t.projectId);

      // Sort by createdAt descending
      todos.sort((a, b) => {
        const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return db - da;
      });

      this._todosByUserCache = { userId, todos, cacheKey };
    }

    // Client-side pagination using offset encoded as nextToken
    const offset = opts.nextToken ? parseInt(opts.nextToken, 10) : 0;
    const all = this._todosByUserCache.todos;
    const page = all.slice(offset, offset + opts.limit);
    const newOffset = offset + opts.limit;
    const hasMore = newOffset < all.length;

    return {
      todos: page,
      nextToken: hasMore ? String(newOffset) : null,
    };
  }

  /**
   * Legacy: fetch assigned todos by IDs (fallback, kept for backward compat).
   */
  private _assignedTodosCache: { ids: string[]; todos: Todo[]; cacheKey: string } | null = null;

  private async _fetchAssignedTodosPage(
    ids: string[],
    opts: {
      search?: string; status?: string; priority?: string;
      tenantId?: string; clientId?: string; backlog?: boolean;
      nextToken?: string | null; limit: number;
    }
  ): Promise<{ todos: Todo[]; nextToken: string | null }> {
    const cacheKey = JSON.stringify({
      ids: ids.sort(),
      search: opts.search, status: opts.status, priority: opts.priority,
      tenantId: opts.tenantId, clientId: opts.clientId, backlog: opts.backlog,
    });

    if (!this._assignedTodosCache || this._assignedTodosCache.cacheKey !== cacheKey) {
      const selectionSet = ['id', 'name', 'nameLower', 'description', 'status', 'priority', 'dueDate',
        'createdAt', 'updatedAt', 'tenantId', 'clientId', 'projectId', 'creatorId', 'validatedBy', 'validatedAt', 'isDeleted'];

      const CONCURRENCY = 25;
      const allTodos: Todo[] = [];

      for (let i = 0; i < ids.length; i += CONCURRENCY) {
        const chunk = ids.slice(i, i + CONCURRENCY);
        const results = await Promise.all(
          chunk.map(async (id) => {
            try {
              const { data } = await this.client.models.Todo.get(
                { id },
                { selectionSet } as any
              );
              return data;
            } catch {
              return null;
            }
          })
        );
        for (const t of results) {
          if (!t) continue;
          allTodos.push({
            id: t.id as string,
            name: (t as any).name,
            description: (t as any).description,
            status: (t as any).status,
            priority: (t as any).priority,
            dueDate: (t as any).dueDate,
            createdAt: (t as any).createdAt,
            updatedAt: (t as any).updatedAt,
            tenantId: (t as any).tenantId ?? undefined,
            clientId: (t as any).clientId ?? undefined,
            projectId: (t as any).projectId ?? undefined,
            creatorId: (t as any).creatorId ?? undefined,
            nameLower: (t as any).nameLower ?? undefined,
            validatedBy: (t as any).validatedBy ?? undefined,
            validatedAt: (t as any).validatedAt ?? undefined,
            isDeleted: (t as any).isDeleted ?? undefined,
          });
        }
      }

      let filtered = allTodos.filter(t => (t as any).isDeleted !== true);
      if (opts.search?.trim()) {
        const q = opts.search.trim().toLowerCase();
        filtered = filtered.filter(t => t.nameLower?.includes(q) || t.name?.toLowerCase().includes(q));
      }
      if (opts.status) filtered = filtered.filter(t => t.status === opts.status);
      if (opts.priority) filtered = filtered.filter(t => t.priority === opts.priority);
      if (opts.tenantId) filtered = filtered.filter(t => t.tenantId === opts.tenantId);
      if (opts.clientId) filtered = filtered.filter(t => t.clientId === opts.clientId);
      if (opts.backlog) filtered = filtered.filter(t => !t.tenantId && !t.clientId && !t.projectId);

      filtered.sort((a, b) => {
        const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return db - da;
      });

      this._assignedTodosCache = { ids, todos: filtered, cacheKey };
    }

    const offset = opts.nextToken ? parseInt(opts.nextToken, 10) : 0;
    const allFiltered = this._assignedTodosCache.todos;
    const page = allFiltered.slice(offset, offset + opts.limit);
    const newOffset = offset + opts.limit;
    const hasMore = newOffset < allFiltered.length;

    return {
      todos: page,
      nextToken: hasMore ? String(newOffset) : null,
    };
  }

  // CLIENT OPERATIONS
  async createClient(client: Partial<Client>): Promise<Client | null> {
    try {
      console.log('[ClientService] Creating client with data:', client);

      // Validate email format if provided
      if (client.email && !this.isValidEmail(client.email)) {
        throw new Error('Invalid email format');
      }

      const { data, errors } = await this.client.models.Client.create({
        name: client.name!,
        type: client.type ?? 'CLIENT',
        email: client.email && client.email.trim() ? client.email : undefined,
        phone: client.phone && client.phone.trim() ? client.phone : undefined,
        address: client.address ? JSON.stringify(client.address) : undefined,
        fullAddress: client.fullAddress && client.fullAddress.trim() ? client.fullAddress : undefined,
        ...(client.tenantId ? { tenantId: client.tenantId } : {}),
      });

      // Also create TenantClient link(s) for many-to-many
      if (data && client.tenantId) {
        await this.linkClientToTenant((data as any).id, client.tenantId).catch(e =>
          console.warn('[ClientService] Failed to create TenantClient link:', e)
        );
      }
      if (data && client.tenantIds?.length) {
        for (const tId of client.tenantIds) {
          await this.linkClientToTenant((data as any).id, tId).catch(e =>
            console.warn('[ClientService] Failed to create TenantClient link:', e)
          );
        }
      }

      if (errors && errors.length) {
        console.error("[ClientService] Error creating client:", errors);
        throw new Error(errors[0].message);
      }

      console.log('[ClientService] Client created successfully:', data);
      // observeQuery will automatically update the list
      return data as unknown as Client;
    } catch (e) {
      console.error("[ClientService] Failed to create client:", e);
      throw e;
    }
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  async updateClient(id: string, updates: Partial<Client>): Promise<Client | null> {
    try {
      console.log('[ClientService] Updating client:', id, 'with data:', updates);

      // Validate email format if provided
      if (updates.email && !this.isValidEmail(updates.email)) {
        throw new Error('Invalid email format');
      }

      // Build update object with only defined fields
      const updateData: any = { id };

      if (updates.name !== undefined) {
        updateData.name = updates.name;
      }
      if (updates.type !== undefined) {
        updateData.type = updates.type || 'CLIENT';
      }
      if (updates.email !== undefined) {
        updateData.email = updates.email && updates.email.trim() ? updates.email : undefined;
      }
      if (updates.phone !== undefined) {
        updateData.phone = updates.phone && updates.phone.trim() ? updates.phone : undefined;
      }
      if (updates.address !== undefined) {
        updateData.address = updates.address ? JSON.stringify(updates.address) : undefined;
      }
      if (updates.fullAddress !== undefined) {
        updateData.fullAddress = updates.fullAddress && updates.fullAddress.trim() ? updates.fullAddress : undefined;
      }
      if ('tenantId' in updates) {
        updateData.tenantId = updates.tenantId || null;
      }

      console.log('[ClientService] Prepared update data:', updateData);

      const { data, errors } = await this.client.models.Client.update(updateData);

      if (errors && errors.length) {
        console.error("[ClientService] Error updating client:", errors);
        throw new Error(errors[0].message);
      }

      console.log('[ClientService] Client updated successfully:', data);
      // observeQuery will automatically update the list
      return data as unknown as Client;
    } catch (e) {
      console.error("[ClientService] Failed to update client:", e);
      throw e;
    }
  }

  async deleteClient(id: string): Promise<boolean> {
    try {
      console.log('[ClientService] Soft-deleting client:', id);

      const { errors } = await this.client.models.Client.update({
        id,
        isDeleted: true,
        deletedAt: new Date().toISOString()
      });

      if (errors && errors.length) {
        console.error("[ClientService] Error soft-deleting client:", errors);
        throw new Error(errors[0].message);
      }

      console.log('[ClientService] Client soft-deleted successfully');
      // observeQuery will automatically update the list
      return true;
    } catch (e) {
      console.error("[ClientService] Failed to soft-delete client:", e);
      throw e;
    }
  }

  // TODO OPERATIONS
  async createTodo(todo: Partial<Todo>): Promise<Todo | null> {
    try {
      const createData: any = {
        name: todo.name!,
        description: todo.description || undefined,
        status: (todo.status || 'TODO') as any,
        priority: (todo.priority || 'MEDIUM') as any,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      if (todo.dueDate) createData.dueDate = todo.dueDate;
      if (todo.tenantId) createData.tenantId = todo.tenantId;
      if (todo.clientId) createData.clientId = todo.clientId;
      if (todo.projectId) createData.projectId = todo.projectId;
      if (todo.creatorId) createData.creatorId = todo.creatorId;
      createData.nameLower = todo.name!.toLowerCase();

      const { data, errors } = await this.client.models.Todo.create(createData);
      if (errors && errors.length) throw new Error(errors[0].message);
      this._assignedTodosCache = null; // Invalidate pagination cache
      this._todosByUserCache = null;
      return data as unknown as Todo;
    } catch (e) {
      console.error('[ClientService] Failed to create todo:', e);
      throw e;
    }
  }

  async updateTodo(id: string, updates: Partial<Todo>): Promise<Todo | null> {
    try {
      const updateData: any = { id, updatedAt: new Date().toISOString() };
      if (updates.name !== undefined) {
        updateData.name = updates.name;
        updateData.nameLower = updates.name.toLowerCase();
      }
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.status !== undefined) {
        updateData.status = updates.status;
        // Auto-set validation fields when status changes to DONE
        if (updates.status === 'DONE') {
          const currentUser = this.userService.getCurrentUserSnapshot();
          updateData.validatedBy = currentUser?.id ?? null;
          updateData.validatedAt = new Date().toISOString();
        } else {
          // Clear validation when reverting from DONE
          updateData.validatedBy = null;
          updateData.validatedAt = null;
        }
      }
      if (updates.priority !== undefined) updateData.priority = updates.priority;
      if (updates.dueDate !== undefined) updateData.dueDate = updates.dueDate ?? null;
      if ('tenantId' in updates) updateData.tenantId = updates.tenantId || null;
      if ('clientId' in updates) updateData.clientId = updates.clientId || null;
      if ('projectId' in updates) updateData.projectId = updates.projectId || null;

      const { data, errors } = await this.client.models.Todo.update(updateData);
      if (errors && errors.length) throw new Error(errors[0].message);
      this._assignedTodosCache = null; // Invalidate pagination cache
      this._todosByUserCache = null;
      return data as unknown as Todo;
    } catch (e) {
      console.error('[ClientService] Failed to update todo:', e);
      throw e;
    }
  }


  async getTodosAssignedToUser(userId: string): Promise<string[]> {
    try {
      const allIds: string[] = [];
      let nextToken: string | undefined = undefined;
      do {
        const result: any = await this.client.models.TodoUser.listTodosByUser(
          { userId },
          {
            selectionSet: ['todoId'],
            nextToken,
            limit: 200,
          }
        );
        if (result.errors?.length) {
          console.error('[ClientService] getTodosAssignedToUser error:', result.errors);
        }
        const items = result.data || [];
        allIds.push(...items.map((tu: any) => tu.todoId as string));
        nextToken = result.nextToken ?? undefined;
      } while (nextToken);
      return allIds;
    } catch (e) {
      console.error('[ClientService] getTodosAssignedToUser failed:', e);
      return [];
    }
  }

  async deleteTodo(id: string): Promise<boolean> {
    try {
      console.log('[ClientService] Soft-deleting todo:', id);

      const result = await this.client.models.Todo.update({
        id,
        isDeleted: true,
        deletedAt: new Date().toISOString()
      } as any);

      console.log('[ClientService] Soft-delete result:', JSON.stringify(result, null, 2));

      if (result.errors && result.errors.length) {
        console.error("[ClientService] Error soft-deleting todo:", result.errors);
        throw new Error(result.errors[0].message);
      }

      console.log('[ClientService] Todo soft-deleted successfully, isDeleted:', (result.data as any)?.isDeleted);
      this._assignedTodosCache = null; // Invalidate pagination cache
      this._todosByUserCache = null;
      return true;
    } catch (e) {
      console.error("[ClientService] Failed to soft-delete todo:", e);
      throw e;
    }
  }

  // TODO-CLIENT RELATIONSHIP OPERATIONS
  async linkTodoToClient(todoId: string, clientId: string): Promise<TodoClient | null> {
    try {
      console.log('[ClientService] Linking todo', todoId, 'to client', clientId);

      const { data, errors } = await this.client.models.TodoClient.create({
        todoId,
        clientId
      });

      if (errors && errors.length) {
        console.error("[ClientService] Error linking todo to client:", errors);
        throw new Error(errors[0].message);
      }

      console.log('[ClientService] Todo linked to client successfully');
      return data as unknown as TodoClient;
    } catch (e) {
      console.error("[ClientService] Failed to link todo to client:", e);
      throw e;
    }
  }

  async unlinkTodoFromClient(linkId: string): Promise<boolean> {
    try {
      console.log('[ClientService] Unlinking todo from client, linkId:', linkId);

      const { errors } = await this.client.models.TodoClient.delete({ id: linkId });

      if (errors && errors.length) {
        console.error("[ClientService] Error unlinking todo from client:", errors);
        throw new Error(errors[0].message);
      }

      console.log('[ClientService] Todo unlinked from client successfully');
      return true;
    } catch (e) {
      console.error("[ClientService] Failed to unlink todo from client:", e);
      throw e;
    }
  }

  async getTodosByClient(clientId: string): Promise<any[]> {
    try {
      console.log('[ClientService] Getting todos for client:', clientId);

      const { data, errors } = await this.client.models.TodoClient.listTodosByClient(
        { clientId },
        {
          selectionSet: [
            'id',
            'todoId',
            'clientId',
            'todo.id',
            'todo.name',
            'todo.description',
            'todo.status',
            'todo.priority',
            'todo.dueDate',
            'todo.createdAt',
            'todo.updatedAt',
            'todo.validatedBy',
            'todo.validatedAt',
            'todo.isDeleted'
          ]
        }
      );

      if (errors && errors.length) {
        console.error("[ClientService] Error getting todos for client:", errors);
      }

      // Post-filter: exclude soft-deleted todos
      const filtered = (data || []).filter((link: any) => !link.todo?.isDeleted);
      console.log('[ClientService] Loaded', filtered.length, 'todos for client (after soft-delete filter)');
      return filtered;
    } catch (e) {
      console.error("[ClientService] Failed to get todos for client:", e);
      return [];
    }
  }

  async getClientsByTodo(todoId: string): Promise<any[]> {
    try {
      const { data, errors } = await this.client.models.TodoClient.listClientsByTodo(
        { todoId },
        {
          selectionSet: [
            'id',
            'todoId',
            'clientId',
            'client.id',
            'client.name',
            'client.email',
            'client.phone',
            'client.address',
            'client.fullAddress',
            'client.isDeleted'
          ]
        }
      );
      if (errors && errors.length) {
        console.error("Error getting clients for todo", errors);
      }
      // Post-filter: exclude soft-deleted clients
      return (data || []).filter((link: any) => !link.client?.isDeleted);
    } catch (e) {
      console.error("Failed to get clients for todo", e);
      return [];
    }
  }

  // TODO-USER RELATIONSHIP OPERATIONS
  async linkTodoToUser(todoId: string, userId: string): Promise<TodoUser | null> {
    try {
      console.log('[ClientService] Linking todo', todoId, 'to user', userId);

      const { data, errors } = await this.client.models.TodoUser.create({
        todoId,
        userId
      });

      if (errors && errors.length) {
        console.error("[ClientService] Error linking todo to user:", errors);
        throw new Error(errors[0].message);
      }

      console.log('[ClientService] Todo linked to user successfully');
      return data as unknown as TodoUser;
    } catch (e) {
      console.error("[ClientService] Failed to link todo to user:", e);
      throw e;
    }
  }

  async unlinkTodoFromUser(linkId: string): Promise<boolean> {
    try {
      console.log('[ClientService] Unlinking todo from user, linkId:', linkId);

      const { errors } = await this.client.models.TodoUser.delete({ id: linkId });

      if (errors && errors.length) {
        console.error("[ClientService] Error unlinking todo from user:", errors);
        throw new Error(errors[0].message);
      }

      console.log('[ClientService] Todo unlinked from user successfully');
      return true;
    } catch (e) {
      console.error("[ClientService] Failed to unlink todo from user:", e);
      throw e;
    }
  }

  async getTodosByUser(userId: string): Promise<any[]> {
    try {
      const allLinks: any[] = [];
      let nextToken: string | undefined = undefined;
      do {
        const result: any = await this.client.models.TodoUser.listTodosByUser(
          { userId },
          {
            selectionSet: [
              'id',
              'todoId',
              'userId',
              'todo.id',
              'todo.name',
              'todo.nameLower',
              'todo.description',
              'todo.status',
              'todo.priority',
              'todo.dueDate',
              'todo.createdAt',
              'todo.updatedAt',
              'todo.clientId',
              'todo.tenantId',
              'todo.projectId',
              'todo.creatorId',
              'todo.validatedBy',
              'todo.validatedAt',
              'todo.isDeleted'
            ],
            nextToken,
            limit: 200,
          }
        );
        if (result.errors?.length) {
          console.error("Error getting todos for user", result.errors);
        }
        allLinks.push(...(result.data || []));
        nextToken = result.nextToken ?? undefined;
      } while (nextToken);
      return allLinks;
    } catch (e) {
      console.error("Failed to get todos for user", e);
      return [];
    }
  }

  /** Server-side fetch with optional DONE exclusion.
   *  The GSI is on userId, so we fetch by user then post-filter status
   *  (DynamoDB can't filter on a related model's field). Still avoids
   *  transferring the full todo list — only this user's links. */
  async getTodosByUserFiltered(userId: string, options: { excludeDone?: boolean; tenantId?: string } = {}): Promise<any[]> {
    const all = await this.getTodosByUser(userId);
    // Post-filter: exclude soft-deleted todos (can't filter related model server-side via join table)
    let filtered = all.filter(link => link.todo && link.todo.isDeleted !== true);
    if (options.excludeDone) {
      filtered = filtered.filter(link => link.todo && link.todo.status !== 'DONE');
    }
    if (options.tenantId) {
      filtered = filtered.filter(link => link.todo && link.todo.tenantId === options.tenantId);
    }
    return filtered;
  }

  async getProjectsByClient(clientId: string): Promise<any[]> {
    try {
      const { data, errors } = await this.client.models.Client.get(
        { id: clientId },
        {
          selectionSet: [
            'id',
            'projects.id',
            'projects.projectName',
            'projects.startDate',
            'projects.clientId',
            'projects.isDeleted'
          ]
        }
      );
      if (errors && errors.length) {
        console.error('[ClientService] Error getting projects for client:', errors);
      }
      // Post-filter: exclude soft-deleted projects
      const projects = (data as any)?.projects || [];
      return projects.filter((p: any) => !p.isDeleted);
    } catch (e) {
      console.error('[ClientService] Failed to get projects for client:', e);
      return [];
    }
  }

  async getUsersByTodo(todoId: string): Promise<any[]> {
    try {
      const { data, errors } = await this.client.models.TodoUser.listUsersByTodo(
        { todoId },
        {
          selectionSet: [
            'id',
            'todoId',
            'userId',
            'user.id',
            'user.name',
            'user.firstName',
            'user.email'
          ]
        }
      );
      if (errors && errors.length) {
        console.error("Error getting users for todo", errors);
      }
      return data || [];
    } catch (e) {
      console.error("Failed to get users for todo", e);
      return [];
    }
  }

  async searchProjects(tenantIds: string[]): Promise<any[]> {
    try {
      const softDeleteFilter = { isDeleted: { ne: true } };
      const filter: any = tenantIds.length > 0
        ? { and: [softDeleteFilter, { or: tenantIds.map(id => ({ tenantId: { eq: id } })) }] }
        : softDeleteFilter;
      const allData: any[] = [];
      let nextToken: string | undefined = undefined;
      do {
        const result: any = await (this.client.models.Project.list as any)({
          filter,
          selectionSet: ['id', 'projectName', 'clientId', 'tenantId'],
          nextToken,
          limit: 200,
        });
        if (result.errors?.length) {
          console.error('[ClientService] searchProjects error:', result.errors);
        }
        allData.push(...(result.data || []));
        nextToken = result.nextToken ?? undefined;
      } while (nextToken);
      return allData;
    } catch (e) {
      console.error('[ClientService] searchProjects failed:', e);
      return [];
    }
  }

  // ══════════════════════════════════════════════════════════════════════
  // TENANT-CLIENT RELATIONSHIP OPERATIONS (Many-to-Many)
  // ══════════════════════════════════════════════════════════════════════

  async linkClientToTenant(clientId: string, tenantId: string): Promise<TenantClientLink | null> {
    try {
      // Direct create — DynamoDB handles idempotency at the application level
      const { data, errors } = await this.client.models.TenantClient.create({
        clientId,
        tenantId,
      });

      if (errors && errors.length) {
        console.error('[ClientService] Error linking client to tenant:', errors);
        throw new Error(errors[0].message);
      }

      return data as unknown as TenantClientLink;
    } catch (e) {
      console.error('[ClientService] Failed to link client to tenant:', e);
      throw e;
    }
  }

  async unlinkClientFromTenant(linkId: string): Promise<boolean> {
    try {
      console.log('[ClientService] Unlinking client from tenant, linkId:', linkId);

      const { errors } = await this.client.models.TenantClient.delete({ id: linkId });

      if (errors && errors.length) {
        console.error('[ClientService] Error unlinking client from tenant:', errors);
        throw new Error(errors[0].message);
      }

      console.log('[ClientService] Client unlinked from tenant successfully');
      return true;
    } catch (e) {
      console.error('[ClientService] Failed to unlink client from tenant:', e);
      throw e;
    }
  }

  async getTenantsForClient(clientId: string): Promise<TenantClientLink[]> {
    try {
      const allLinks: TenantClientLink[] = [];
      let nextToken: string | undefined = undefined;
      do {
        const result: any = await this.client.models.TenantClient.listTenantsByClient(
          { clientId },
          {
            selectionSet: ['id', 'tenantId', 'clientId', 'tenant.id', 'tenant.name'],
            limit: 200,
            nextToken,
          }
        );
        if (result.errors?.length) {
          console.error('[ClientService] getTenantsForClient error:', result.errors);
        }
        for (const link of (result.data || [])) {
          allLinks.push({
            id: link.id,
            tenantId: link.tenantId,
            clientId: link.clientId,
            tenant: link.tenant ?? null,
          });
        }
        nextToken = result.nextToken ?? undefined;
      } while (nextToken);
      return allLinks;
    } catch (e) {
      console.error('[ClientService] getTenantsForClient failed:', e);
      return [];
    }
  }

  async getClientsByTenantLink(tenantId: string): Promise<TenantClientLink[]> {
    try {
      const allLinks: TenantClientLink[] = [];
      let nextToken: string | undefined = undefined;
      do {
        const result: any = await this.client.models.TenantClient.listClientsByTenant(
          { tenantId },
          {
            selectionSet: ['id', 'tenantId', 'clientId'],
            limit: 200,
            nextToken,
          }
        );
        if (result.errors?.length) {
          console.error('[ClientService] getClientsByTenantLink error:', result.errors);
        }
        for (const link of (result.data || [])) {
          allLinks.push({
            id: link.id,
            tenantId: link.tenantId,
            clientId: link.clientId,
          });
        }
        nextToken = result.nextToken ?? undefined;
      } while (nextToken);
      return allLinks;
    } catch (e) {
      console.error('[ClientService] getClientsByTenantLink failed:', e);
      return [];
    }
  }

  /**
   * Sync client-tenant links: set the exact list of tenantIds for a client.
   * Adds missing links and removes stale ones — all operations run in parallel.
   */
  async syncClientTenants(clientId: string, tenantIds: string[]): Promise<void> {
    const currentLinks = await this.getTenantsForClient(clientId);
    const currentTenantIds = new Set(currentLinks.map(l => l.tenantId));
    const desiredTenantIds = new Set(tenantIds);

    const ops: Promise<any>[] = [];

    // Add missing links (parallel)
    for (const tId of desiredTenantIds) {
      if (!currentTenantIds.has(tId)) {
        ops.push(this.linkClientToTenant(clientId, tId));
      }
    }

    // Remove stale links (parallel)
    for (const link of currentLinks) {
      if (!desiredTenantIds.has(link.tenantId)) {
        ops.push(this.unlinkClientFromTenant(link.id));
      }
    }

    await Promise.all(ops);
  }
}
